import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
  Calendar as CalendarComponent,
  momentLocalizer
} from "react-big-calendar";
import SweetAlert from "react-bootstrap-sweetalert";

// @material-ui/core
import withStyles from "@material-ui/core/styles/withStyles";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Modal from "@material-ui/core/Modal";
import Box from "@material-ui/core/Box";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import TextField from "@material-ui/core/TextField";

import Heading from "components/Heading/Heading.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import Table from "components/Table/Table.jsx";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import CustomSelect from "components/CustomSelect/CustomSelect.jsx";
import {
  prefersDarkModeMediaQuery,
  darkModeStyleBlocks
} from "assets/jss/material-dashboard-react.jsx";

import { getActivities } from "services/userDataProvider.js";
import {
  getUserCalendar,
  createEvent,
  deleteEvent,
  getEventDetails,
  moveEvent
} from "../../services/calendarDataProvider";
import { getMyUsers } from "../../services/advisorUsers.js";

const styles = {
  trainingsCalenderContainer: {
    padding: "0px !important"
  },
  trainingsCardHeader: {
    padding: "15px !important",
    marginTop: "-20px !important",
    borderRadius: "3px !important",
    background: "linear-gradient(60deg, #275b95, #00acc1) !important",
    boxShadow:
      "0 4px 20px 0 rgba(0, 0, 0,.14), 0 7px 10px -5px rgba(0, 172, 193,.4) !important"
  },
  actionButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    justifyContent: "center"
  },
  moveEventModal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    padding: "24px",
    minWidth: "400px",
    maxWidth: "90vw",
    [prefersDarkModeMediaQuery]: darkModeStyleBlocks.calendar.moveEventModal
  }
};

const localizer = momentLocalizer(moment);

const EVENT_TRAINING = "Training";
const EVENT_ACTIVITY = "Activity";
const EVENT_REST = "Rest";

class Calendar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      value: 0,
      sessions: [],
      actionMenuAnchor: null,
      selectedEventForAction: null,
      moveEventModalOpen: false,
      moveOption: "single",
      targetDate: moment().format("YYYY-MM-DD"),
      isMovingEvent: false,
      moveSuccessMessage: null,
      moveErrorMessage: null
    };
  }

  handleEventClick = (event, e) => {
    this.setState({
      actionMenuAnchor: e.currentTarget,
      selectedEventForAction: event
    });
  };

  handleCloseActionMenu = () => {
    this.setState({
      actionMenuAnchor: null
    });
  };

  handleShowDetails = () => {
    const event = this.state.selectedEventForAction;
    this.handleCloseActionMenu();
    this.selectedEvent(event);
  };

  handleOpenMoveModal = () => {
    this.setState({
      moveEventModalOpen: true,
      targetDate: moment(this.state.selectedEventForAction.start).format(
        "YYYY-MM-DD"
      )
    });
    this.handleCloseActionMenu();
  };

  handleCloseMoveModal = () => {
    this.setState({
      moveEventModalOpen: false,
      moveOption: "single",
      isMovingEvent: false,
      moveSuccessMessage: null,
      moveErrorMessage: null
    });
  };

  handleMoveOptionChange = e => {
    this.setState({
      moveOption: e.target.value
    });
  };

  handleTargetDateChange = e => {
    this.setState({
      targetDate: e.target.value
    });
  };

  handleMoveEvent = () => {
    const { selectedEventForAction, moveOption, targetDate } = this.state;

    this.setState({
      isMovingEvent: true,
      moveSuccessMessage: null,
      moveErrorMessage: null
    });

    const eventData = {
      event_id: selectedEventForAction.event.id,
      move_type: moveOption,
      target_date: targetDate,
      user_id: this.state.userId
    };

    moveEvent(this.props.axios, eventData)
      .then(response => {
        // Update calendar with events from response
        const updatedEvents = response.events || [];
        const existingSessions = this.state.sessions.filter(
          session =>
            !updatedEvents.some(
              updatedEvent => updatedEvent.id === session.event.id
            )
        );

        const newSessions = updatedEvents.map(event => ({
          event: {
            ...event,
            type: event.title.includes("Rest") ? EVENT_REST : EVENT_TRAINING
          },
          title: event.title,
          start: new Date(event.start),
          end: new Date(event.end),
          type: event.title.includes("Rest") ? EVENT_REST : EVENT_TRAINING
        }));

        this.setState({
          sessions: [...existingSessions, ...newSessions],
          isMovingEvent: false,
          moveSuccessMessage: response.message || "Event moved successfully"
        });

        // Auto-close modal after 2 seconds
        setTimeout(() => {
          this.handleCloseMoveModal();
        }, 2000);
      })
      .catch(error => {
        this.setState({
          isMovingEvent: false,
          moveErrorMessage:
            error.error ||
            error.message ||
            "Failed to move the event. Please try again."
        });
      });
  };

  renderMoveEventModal() {
    const { classes, user } = this.props;
    const isAdvisor = user.roles && user.roles.includes("ADVISOR");
    const {
      moveEventModalOpen,
      moveOption,
      targetDate,
      selectedEventForAction,
      isMovingEvent,
      moveSuccessMessage,
      moveErrorMessage
    } = this.state;

    if (!selectedEventForAction) return null;

    return (
      <Modal
        open={moveEventModalOpen}
        onClose={this.handleCloseMoveModal}
        aria-labelledby="move-event-modal"
      >
        <Box className={classes.moveEventModal}>
          <h3 style={{ marginTop: 0, marginBottom: "20px" }}>Move Training</h3>

          {moveSuccessMessage && (
            <div
              style={{
                backgroundColor: "#e8f5e9",
                color: "#2e7d32",
                padding: "12px",
                borderRadius: "4px",
                marginBottom: "16px",
                fontSize: "14px",
                fontWeight: "500"
              }}
            >
              ✓ {moveSuccessMessage}
            </div>
          )}

          {moveErrorMessage && (
            <div
              style={{
                backgroundColor: "#ffebee",
                color: "#c62828",
                padding: "12px",
                borderRadius: "4px",
                marginBottom: "16px",
                fontSize: "14px"
              }}
            >
              ✗ {moveErrorMessage}
            </div>
          )}

          <div style={{ marginBottom: "20px" }}>
            <strong>Current Event:</strong> {selectedEventForAction.event.title}
            <br />
            <strong>Current Date:</strong>{" "}
            {moment(selectedEventForAction.start).format(
              "MMMM Do YYYY, hh:mm a"
            )}
          </div>

          <FormControl
            component="fieldset"
            style={{ marginBottom: "20px", width: "100%" }}
            disabled={isMovingEvent || moveSuccessMessage}
          >
            <FormLabel component="legend">Move Options</FormLabel>
            <RadioGroup
              value={moveOption}
              onChange={this.handleMoveOptionChange}
            >
              <FormControlLabel
                value="single"
                control={<Radio color="primary" />}
                label="Move this training only"
              />
              {isAdvisor && (
                <FormControlLabel
                  value="all"
                  control={<Radio color="primary" />}
                  label="Move this and all successive trainings"
                />
              )}
            </RadioGroup>
          </FormControl>

          <TextField
            label="Target Date"
            type="date"
            fullWidth
            value={targetDate}
            onChange={this.handleTargetDateChange}
            InputLabelProps={{
              shrink: true
            }}
            style={{ marginBottom: "20px" }}
            disabled={isMovingEvent || moveSuccessMessage}
          />

          <div
            style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
          >
            <Button
              color="default"
              onClick={this.handleCloseMoveModal}
              disabled={isMovingEvent}
            >
              {moveSuccessMessage ? "Close" : "Cancel"}
            </Button>
            {!moveSuccessMessage && (
              <Button
                color="info"
                onClick={this.handleMoveEvent}
                disabled={isMovingEvent}
              >
                {isMovingEvent ? "Moving..." : "Move Event"}
              </Button>
            )}
          </div>
        </Box>
      </Modal>
    );
  }

  getUserActivities = (startTime, endTime) => {
    getActivities(
      this.props.axios,
      this.state.userId,
      null,
      false,
      null,
      startTime,
      endTime
    ).then(data => {
      let activitySessions = data.results.map(act => {
        return {
          event: {
            description: act.activity_name,
            end: act.start_time_gmt,
            start: act.start_time_gmt,
            title: act.activity_name,
            type: EVENT_ACTIVITY,
            ...act
          },
          title: act.activity_name,
          start: moment(act.start_time_gmt).toDate(),
          end: moment(act.start_time_gmt)
            .add(act.duration_seconds, "seconds")
            .toDate(),
          type: EVENT_ACTIVITY
        };
      });
      const updatedSessions = [...this.state.sessions, ...activitySessions];
      this.setState({
        activities: data.results,
        sessions: updatedSessions
      });
    });
  };

  componentDidMount() {
    const fromTime = moment()
      .clone()
      .startOf("month")
      .format(moment.defaultFormatUtc);
    const toTime = moment()
      .clone()
      .endOf("month")
      .format(moment.defaultFormatUtc);

    getUserCalendar(this.props.axios, this.state.userId, fromTime, toTime).then(
      data => {
        let allSessions = [];
        data.forEach(calendarData => {
          calendarData.occurences.forEach(session => {
            return allSessions.push({
              event: {
                ...session.event,
                title: session.event.title,
                type: session.event.title.includes("Rest")
                  ? EVENT_REST
                  : EVENT_TRAINING
              },
              title: session.event.title,
              start: new Date(session.event.start),
              end: new Date(session.event.end),
              type: session.event.title.includes("Rest")
                ? EVENT_REST
                : EVENT_TRAINING
            });
          });
        });
        this.setState({ userId: null, sessions: allSessions }, () => {
          this.getUserActivities(fromTime, toTime);
        });
      }
    );

    getMyUsers(this.props.axios).then(data => {
      this.setState({
        users: data
      });
    });
  }

  calendarTimeRangeChanged = ({ start, end }) => {
    let fromTime = moment(start)
      .utc()
      .format(moment.defaultFormatUtc);
    let toTime = moment(end)
      .utc()
      .format(moment.defaultFormatUtc);
    getUserCalendar(this.props.axios, this.state.userId, fromTime, toTime).then(
      data => {
        let allSessions = [];
        data.forEach(calendarData => {
          calendarData.occurences.forEach(session => {
            return allSessions.push({
              event: {
                ...session.event,
                type: session.event.title.includes("Rest")
                  ? EVENT_REST
                  : EVENT_TRAINING
              },
              title: session.event.title,
              start: new Date(session.event.start),
              end: new Date(session.event.end),
              type: session.event.title.includes("Rest")
                ? EVENT_REST
                : EVENT_TRAINING
            });
          });
        });
        this.setState({ sessions: allSessions }, () => {
          this.getUserActivities(fromTime, toTime);
        });
      }
    );
  };

  handleClick = (name, idx) => e => {
    if (name === "cancel") {
      deleteEvent(this.props.axios, idx).then(e => {
        let sessions = this.state.sessions.filter(
          session => idx !== session.event.id
        );
        this.setState({ sessions: sessions, selectedEvent: undefined });
      });
    } else if (name === "details") {
      getEventDetails(this.props.axios, idx);
    } else if (name === "back") {
      this.setState({ selectedEvent: undefined });
    }
  };

  selectedEvent = event => {
    const currentSessionIdx = this.state.sessions.findIndex(
      et => et.event.id === event.event.id
    );
    this.setState({
      currentSessionIdx: currentSessionIdx,
      totalSessions: this.state.sessions.length,
      selectedEvent: event
    });
  };

  selectNextEvent() {
    let currentSessionIdx = this.state.currentSessionIdx || 0;
    this.setState({
      currentSessionIdx: currentSessionIdx + 1,
      selectedEvent: this.state.sessions[currentSessionIdx + 1]
    });
  }

  selectPreviousEvent() {
    let currentSessionIdx = this.state.currentSessionIdx || 0;
    this.setState({
      currentSessionIdx: currentSessionIdx - 1,
      selectedEvent: this.state.sessions[currentSessionIdx - 1]
    });
  }

  setAlert(alert) {
    this.setState({ alert: alert });
  }

  hideAlert = () => {
    this.setAlert(null);
  };

  addNewEventAlert = slotInfo => {
    const { classes } = this.props;
    this.setAlert(
      <SweetAlert
        input
        showCancel
        style={{ display: "block", marginTop: "-100px" }}
        title="Enter session title"
        onConfirm={e => this.addNewEvent(e, slotInfo)}
        onCancel={() => this.hideAlert()}
        confirmBtnCssClass={classes.button + " " + classes.success}
        cancelBtnCssClass={classes.button + " " + classes.danger}
      />
    );
  };

  addNewEvent = (e, slotInfo) => {
    let eventData = {
      title: e,
      start: slotInfo.start,
      end: slotInfo.end
    };
    let newEvents = this.state.sessions;
    createEvent(this.props.axios, eventData).then(data => {
      newEvents.push({
        event: data,
        start: slotInfo.start,
        end: slotInfo.end
      });
      this.setAlert(null);
    });
    this.setState({ sessions: newEvents });
  };

  handleUserChange = value => {
    const fromTime = moment()
      .clone()
      .startOf("month")
      .format(moment.defaultFormatUtc);
    const toTime = moment()
      .clone()
      .endOf("month")
      .format(moment.defaultFormatUtc);
    this.setState(
      {
        userId: value,
        sessions: []
      },
      () => {
        getUserCalendar(
          this.props.axios,
          this.state.userId,
          fromTime,
          toTime
        ).then(data => {
          let allSessions = [];
          data.forEach(calendarData => {
            calendarData.occurences.forEach(session => {
              return allSessions.push({
                event: {
                  ...session.event
                },
                title: session.event.title,
                start: new Date(session.event.start),
                end: new Date(session.event.end),
                type: session.event.title.includes("Rest")
                  ? EVENT_REST
                  : EVENT_TRAINING
              });
            });
          });
          this.setState({ sessions: allSessions }, () => {
            this.getUserActivities(fromTime, toTime);
          });
        });
      }
    );
  };

  renderPlanDescription = description => {
    const keyNames = ["Warm up:", "Workout:", "Cool down:"];
    if (description && !description.includes(";;;")) {
      keyNames.forEach(keyName => {
        description = description.replace(keyName, ";;; " + keyName);
      });
    }
    const lines = description.split(";;;");
    return (
      <ul>
        {lines.filter(line => line.trim().length > 0).map((line, idx) => {
          return <li key={idx}>{line}</li>;
        })}
      </ul>
    );
  };

  renderSession(sessionData, showCancel) {
    const { classes } = this.props;
    if (sessionData.event === undefined) {
      return null;
    }
    let trainingType = sessionData.event.title;
    let trainingPlan = this.renderPlanDescription(
      sessionData.event.description
    );
    let advisor = sessionData.event.creator;

    let tableData = [];

    tableData.push(["Event", `${trainingType}`]);
    if (
      sessionData.event.type === EVENT_TRAINING ||
      sessionData.event.type === EVENT_ACTIVITY ||
      sessionData.event.type === EVENT_REST
    ) {
      tableData.push([
        "Time",
        `${moment(sessionData.start).format(
          "MMMM Do YYYY, hh:mm a"
        )} till ${moment(sessionData.end).format("hh:mm a")}`
      ]);
    }
    if (
      sessionData.event.type === EVENT_TRAINING ||
      sessionData.event.color_event === EVENT_TRAINING
    ) {
      tableData.push(["Plan", trainingPlan]);
    }

    return (
      <Card className={classes.trainingsCalenderContainerCard}>
        <CardHeader className={classes.trainingsCardHeader} stats>
          <h3 className={classes.cardCategory}>{sessionData.event.title}</h3>
          <p className={classes.cardTitle}>
            {moment(sessionData.start).fromNow()}
          </p>
        </CardHeader>
        <CardBody className={classes.trainingsCalenderContainerContainer}>
          <Table tableHeaderColor="info" tableData={tableData} />
        </CardBody>
        <CardFooter>
          <div className={classes.actionButtons}>
            <Button
              color="info"
              onClick={this.handleClick("back", sessionData.event.id)}
            >
              Back
            </Button>
            <Button
              color="info"
              onClick={() => this.selectPreviousEvent()}
              disabled={this.state.currentSessionIdx <= 0}
            >
              Previous
            </Button>
            <Button
              color="info"
              onClick={() => this.selectNextEvent()}
              disabled={
                this.state.currentSessionIdx >= this.state.totalSessions
              }
            >
              Next
            </Button>
            {sessionData.event.type === EVENT_TRAINING && (
              <Button
                color="warning"
                disabled={advisor === "" || !showCancel}
                onClick={this.handleClick("cancel", sessionData.event.id)}
              >
                Cancel Session
              </Button>
            )}
          </div>
        </CardFooter>
      </Card>
    );
  }

  renderUsersSelectionBox() {
    let userOptions =
      this.state.users &&
      this.state.users.map(user => {
        return { text: user.username, value: user.id };
      });
    return (
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <CustomSelect
            labelText={"User"}
            items={userOptions}
            onChange={this.handleUserChange}
          />
        </GridItem>
      </GridContainer>
    );
  }

  renderCalendar() {
    const { classes } = this.props;
    const calendarEvents = this.state.sessions || [];

    const propGetter = (event, start, end, isSelected) => {
      return {
        style: {
          backgroundColor:
            event.type === EVENT_ACTIVITY
              ? "#81c784"
              : event.type === EVENT_REST
                ? "#ffb74d"
                : "#64b5f6",
          display: "flex",
          alignItems: "center",
          padding: "5px",
          borderRadius: "4px"
        }
      };
    };

    return (
      <Card className={classes.trainingsCalenderContainerCard}>
        <CardBody calendar className={classes.trainingsCalenderContainer}>
          <CalendarComponent
            selectable
            localizer={localizer}
            events={calendarEvents}
            defaultView="month"
            scrollToTime={new Date(2020, 1, 1, 1)}
            defaultDate={new Date()}
            onSelectEvent={(event, e) => this.handleEventClick(event, e)}
            onSelectSlot={slotInfo => this.addNewEventAlert(slotInfo)}
            eventPropGetter={propGetter}
            views={["month", "agenda"]}
            onRangeChange={this.calendarTimeRangeChanged}
          />
        </CardBody>
      </Card>
    );
  }

  render() {
    const { user } = this.props;
    const isAdvisor = user.roles && user.roles.includes("ADVISOR");
    const { actionMenuAnchor, selectedEventForAction } = this.state;

    return (
      <div>
        <Heading
          textAlign="center"
          title="Your Trainings Calendar"
          category={
            <span>
              The calendar also shows free slots from your advisor, for you to
              book sessions.
            </span>
          }
        />
        {isAdvisor ? this.renderUsersSelectionBox() : null}
        {this.state.alert}

        {/* Action Menu */}
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={this.handleCloseActionMenu}
        >
          <MenuItem onClick={this.handleShowDetails}>See Details</MenuItem>
          {selectedEventForAction &&
            selectedEventForAction.type === EVENT_TRAINING && (
              <MenuItem onClick={this.handleOpenMoveModal}>
                Move Training
              </MenuItem>
            )}
        </Menu>

        {/* Move Event Modal */}
        {this.renderMoveEventModal()}

        {this.state.selectedEvent !== undefined
          ? this.renderSession(this.state.selectedEvent, true)
          : this.renderCalendar()}
      </div>
    );
  }
}

Calendar.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(Calendar);
